/* Login business logic flow
 * https://drive.google.com/file/d/1vWFytAhp4GKoNgG9OSZI-GPm0LIzdfi1/view
 */

import NetInfo from '@react-native-community/netinfo';
import messaging from '@react-native-firebase/messaging';

import { IApiError } from '@frontend/request';

import { aqxTraderSDKConfig } from '@aquariuxfintech/aqx-trader-sdk-config';

import { accountAPI } from '@aqx-core/common/api/account';
import { EPasswordType } from '@aqx-core/common/api/account-link';
import {
  DEMO_LOGINS,
  ELoginAs,
  ELoginSource,
  IChangePasswordParams,
  IExperienceLoginParams,
  ILoginErrorResponse,
  ILoginResponse,
  LIVE_LOGINS,
  authenticationAPIController
} from '@aqx-core/common/api/auth';
import { ELoginConfigType } from '@aqx-core/common/api/config';
import { IAPIControllerCallbacks } from '@aqx-core/common/api/types';
import {
  ESimple2TradeAccountType,
  ISwitchMetatraderAccount,
  ISwitchMetatraderAccountParams,
  ITradingAccount,
  IUserDetail,
  userAccountAPIController
} from '@aqx-core/common/api/user-account';
import { userAccountSettingAPIController } from '@aqx-core/common/api/user-account-setting';
import configs from '@aqx-core/common/configs';
import { i18n } from '@aqx-core/common/i18n';
import { ECommonResponseCode, mapResponseCodeToMessage } from '@aqx-core/common/request';
import { ITradeCashPositionLiveData, ITradeTradingPowerLiveData, socketService } from '@aqx-core/common/socket';
import storage, { EStorageKey } from '@aqx-core/common/storage';
import { validateCompanyAccess } from '@aqx-core/common/utils/company-access';
import { checkIsNative, isEmpty, isPropInvalid } from '@aqx-core/common/validators';

import { broadcastTradingAccountChangeMessage } from '../../hooks/syncing/tradingAccount';
import { companyService } from '../company';
import { companyViewController } from '../company/controller';
import { mixpanelAnalysisService } from '../mixpanel';
import { EMixpanelProperties } from '../mixpanel/types';
import { EUISnackBarMessageType, snackbarController } from '../snackbar';
import { equityChartService } from '../trade/analysis/equity-chart/service';
import { tradeFormService } from '../trade/form';
import { omitProperties } from '../utils';
import { authenticationService } from './service';
import {
  IViewHandleLoginResultParams,
  IViewLoginParams,
  IViewLogoutParams,
  IViewTradingAccountBalances
} from './types';
import { extractBalanceFields, getMobileLoginAPICallMap, getWebLoginAPICallMap } from './utils';

class AuthenticationViewController {
  private checkingSession = false;
  private loggingOut = false;
  private sessionInterval?: ReturnType<typeof setInterval>;

  // Context getter
  private get loginAs() {
    return authenticationService.context.loginAs;
  }

  private get companyProfile() {
    return companyService.context?.companyProfile;
  }

  // Public getter
  public getUserDetails = () => authenticationService.context.userDetails;
  public getUserTradingAccounts = () => authenticationService.context.tradingAccounts;
  public getSelectedTradingAccount = () => authenticationService.context.selectedTradingAccountDetails;
  public getStoragedRememberMeState = () => Boolean(storage.getItem(EStorageKey.REMEMBER_ME));

  // Public Methods
  public logoutUser = async (params: IViewLogoutParams) => {
    if (this.loggingOut) {
      return;
    }
    this.loggingOut = true;

    const { loginAsDemo = false, loginAsLive = false, onRedirect, logoutCallback } = params || {};

    this.stopSessionInterval();
    mixpanelAnalysisService.user.logout(false);
    await authenticationAPIController.logout({
      onSuccess: () => {
        socketService.disconnect();
        this.resetUserDetails();
        this.resetStorage();
        equityChartService.resetPeriod();
        this.updateLiveDemoLoginContext({ loginAsDemo, loginAsLive });
        // this.context.navigateToSignUpForm = navigateToSignUpForm; // to be implement with logout callback
        logoutCallback?.forEach((callback) => callback?.());
        companyViewController.getCompanyProfile();
        this.initPrefilledLogin();
        onRedirect?.();

        // Clear all preserved trade form values on logout
        // To avoid leaving cached input values
        // Add timeout so that this is run after any unmount logic to set preserved values
        setTimeout(() => {
          tradeFormService.clearPreservedValues();
        }, 1000);

        this.loggingOut = false;
      },
      onError: (error) => {
        this.loggingOut = false;
        const { code, message } = error as IApiError;
        if (code === ECommonResponseCode.UNAUTHORIZED) {
          socketService.disconnect();
          this.resetUserDetails();
        }
        throw new Error(mapResponseCodeToMessage(code, message));
      }
    });
  };

  public initWithToken = (token: string, onComplete: () => void) => {
    storage.setItem(EStorageKey.TOKEN, token);
    onComplete();
  };

  public init = async (source: ELoginSource | undefined) => {
    authenticationService.setLoading(true);
    if (source) {
      authenticationService.setSource(source);
    }
    this.fetchAccount();
  };

  public fetchAccount = async () => {
    try {
      await userAccountAPIController.getAccounts({
        onSuccess: async (result) => {
          if (result && result.tradingAccounts.length > 0) {
            authenticationService.setUserDetails(result);
            authenticationService.setTradingAccounts(result.tradingAccounts);
            this.setLoginAs(this.getLoginAs(result.loginConfig));

            const selectedAccount = this.findSelectedAccount(result.tradingAccounts);

            if (selectedAccount) {
              this.startSessionInterval();
              storage.setItem(EStorageKey.META_TRADER_ID, selectedAccount.accountNumber);
              storage.setItem(EStorageKey.META_TRADER_GROUP, selectedAccount.userGroup);
              storage.setItem(EStorageKey.MARKET_SUBSCRIPTION, selectedAccount.marketSubcription);

              const balanceKeys = Object.keys(extractBalanceFields(selectedAccount)) as (keyof ITradingAccount)[];
              const mappedTradingAccountDetails = omitProperties(selectedAccount, balanceKeys);
              authenticationService.setTradingAccountDetails(mappedTradingAccountDetails);
              authenticationService.setTradingAccountBalances(extractBalanceFields(selectedAccount));

              // Register super properties
              const tenantId = companyService.getCompanyTenantId();
              const { source, loginAs } = authenticationService.context;
              const mainProductCodeList = companyViewController.getCompanyIntegration();
              mixpanelAnalysisService.superProperties.registerSuperProperties({
                [EMixpanelProperties.SOURCE]: source,
                [EMixpanelProperties.TENANT_ID]:
                  tenantId ??
                  mixpanelAnalysisService.user.getTenantIdFromTraderSubscription(selectedAccount.traderSubscription),
                [EMixpanelProperties.USER_ACCOUNT_TYPE]: loginAs,
                [EMixpanelProperties.IS_DEMO]: selectedAccount.isDemo,
                [EMixpanelProperties.MAIN_PRODUCT_CODE]: mainProductCodeList ?? '-',
                [EMixpanelProperties.METATRADER_GROUP]: selectedAccount.userGroup
              });

              authenticationService.setAuthenticated(true);
              authenticationService.setLoading(false);
            }
          }
        },
        onError: async (error) => {
          const { code, message } = error as IApiError;
          if (checkIsNative()) {
            const state = await NetInfo.fetch();
            const isAlreadyAuthenticated = authenticationService.context.authenticated;
            // If user is offline but already authenticated, do not redirect to login page on mobile
            const shouldRedirect = state.isConnected || !isAlreadyAuthenticated;
            this.onFetchAccountError(shouldRedirect);
          } else {
            this.onFetchAccountError(true);
          }

          if (code === ECommonResponseCode.SI_1018) {
            snackbarController.showAlert({
              messageType: EUISnackBarMessageType.ERROR,
              message: mapResponseCodeToMessage(code)
            });
          }

          throw new Error(mapResponseCodeToMessage(code, message));
        }
      });
    } catch (e: unknown) {
      this.onFetchAccountError(true);
    }
  };

  public login = async (params: IViewLoginParams) => {
    const { errorCallback, onBeforeSuccess, successCallback, ...loginParams } = params;
    authenticationService.setLoginErrorCode(undefined);

    if (checkIsNative() && !params?.deviceId) {
      // eslint-disable-next-line no-console
      console.warn('deviceId is required on native platform');
    }

    if (!loginParams?.source) {
      throw new Error('Source not found');
    }

    authenticationService.setAuthLoading(true);

    await this.getLoginResult(loginParams, {
      onSuccess: (result) => {
        if (result) {
          this.handleLoginResult({
            result,
            onBeforeSuccess,
            successCallback,
            userId: loginParams.userId,
            password: loginParams.password,
            source: loginParams.source
          });
        }
      },
      onError: (error) => {
        const { code, message, result } = error as IApiError;

        let errorCode = code as ECommonResponseCode;

        if (errorCode === ECommonResponseCode.A_1013) {
          const errorResponse: ILoginErrorResponse = result || {};
          authenticationService.setLockedUntil(errorResponse?.lockedUntil);
        }

        if (errorCode === ECommonResponseCode.BAD_REQUEST) {
          errorCode = ECommonResponseCode.A_1006;
        }

        if (code === ECommonResponseCode.AX_1005) {
          authenticationService.setLoginErrorCode(errorCode);
        }

        errorCallback?.(errorCode, mapResponseCodeToMessage(code, message));
        authenticationService.setAuthLoading(false);
      },
      onFinally: () => {
        const isRemembered =
          this.getStoragedRememberMeState() && this.loginAs !== undefined && LIVE_LOGINS.includes(this.loginAs);

        this.syncCredentialsToStorage({
          isRemembered,
          userId: loginParams.userId,
          password: loginParams.password
        });
      }
    });
  };

  public updateAccountLanguage = async (onError: (error: string) => void) => {
    await userAccountSettingAPIController.updateLanguage({
      onSuccess: () => {},
      onError: (error) => {
        const { code, message } = error as IApiError;
        onError(mapResponseCodeToMessage(code, message));
      }
    });
  };

  public initPrefilledLogin = () => {
    const rememberMe = storage.getItem(EStorageKey.REMEMBER_ME) as boolean;
    const userId = storage.getItem(EStorageKey.USER_ID) as string;
    const password = storage.getItem(EStorageKey.PASSWORD) as string;
    authenticationService.setPrefilledLogin(rememberMe ? { userId, password } : undefined);
  };

  public setPrefilledValues = (userId: string, password: string, loginType: ELoginAs) => {
    authenticationService.setLoginAs(loginType);
    authenticationService.setPrefilledLogin({ userId, password });
  };

  public resetPrefilledValues = () => {
    authenticationService.setPrefilledLogin(undefined);
  };

  private getLoginResult = async (params: IViewLoginParams, callbacks: IAPIControllerCallbacks<ILoginResponse>) => {
    const isNative = checkIsNative();
    if (isNative && !params.deviceId) {
      return;
    }

    const loginMap = isNative
      ? getMobileLoginAPICallMap({ ...params, deviceId: params.deviceId! }, callbacks)
      : getWebLoginAPICallMap(params, callbacks);
    if (!this.loginAs) {
      loginMap.LIVE();
      return;
    }

    loginMap[this.loginAs]();
  };

  private handleLoginResult = (params: IViewHandleLoginResultParams) => {
    const { result, onBeforeSuccess, successCallback, source } = params;
    const { token, user } = result || {};

    storage.setItem(EStorageKey.TOKEN, token);

    // To Do: Mixpanel
    if (user) {
      mixpanelAnalysisService.user.login(user);
    }

    onBeforeSuccess?.();

    // To fetch feature announcements after login
    storage.setItem(EStorageKey.SHOULD_SHOW_FEATURE_ANNOUNCEMENT_MODAL, 'true');

    authenticationService.setAuthLoading(false);
    this.init(source);

    // Re-init SDK config on web after login so the SDK's companyService gets
    // populated with the authenticated company profile and the watchlist can init
    if (source === ELoginSource.WEB) {
      aqxTraderSDKConfig.init({
        baseUrl: '',
        socketPathPrefix: `//${location.host}`,
        getToken: () => {
          return storage.getItem(EStorageKey.TOKEN);
        }
      });
    }

    if (source === ELoginSource.ANDROID_APP || source === ELoginSource.IOS_APP) {
      aqxTraderSDKConfig.init({
        baseUrl: configs.baseURL, // @TODO: update via react-native-config
        socketPathPrefix: configs.baseURL,
        getToken: () => {
          return storage.getItem(EStorageKey.TOKEN);
        }
      });
    }

    companyViewController.getCompanyProfile();

    successCallback?.();
  };

  public getLiveLoginTypeFromCompanyAccess = ({
    multiServerEnabled,
    hasMT5LiveLoginConfig,
    hasMT4LiveLoginConfig,
    hasCentroidLiveLoginConfig
  }: {
    multiServerEnabled?: boolean;
    hasMT5LiveLoginConfig: boolean;
    hasMT4LiveLoginConfig: boolean;
    hasCentroidLiveLoginConfig: boolean;
  }) => {
    if (multiServerEnabled && (hasMT4LiveLoginConfig || hasMT5LiveLoginConfig || hasCentroidLiveLoginConfig)) {
      return ELoginAs.OMS_LIVE;
    }
    if (hasMT4LiveLoginConfig) {
      return ELoginAs.MT4_LIVE;
    }
    if (hasMT5LiveLoginConfig) {
      return ELoginAs.MT5_LIVE;
    }
    if (hasCentroidLiveLoginConfig) {
      return ELoginAs.CENTROID_LIVE;
    }
    return null;
  };

  public getDemoLoginTypeFromCompanyAccess = ({
    multiServerEnabled,
    hasMT4DemoLoginConfig,
    hasMT5DemoLoginConfig,
    hasCentroidDemoLoginConfig
  }: {
    multiServerEnabled?: boolean;
    hasMT4DemoLoginConfig: boolean;
    hasMT5DemoLoginConfig: boolean;
    hasCentroidDemoLoginConfig: boolean;
  }) => {
    if (multiServerEnabled && (hasMT4DemoLoginConfig || hasMT5DemoLoginConfig || hasCentroidDemoLoginConfig)) {
      return ELoginAs.OMS_DEMO;
    }
    if (hasMT4DemoLoginConfig) {
      return ELoginAs.MT4_DEMO;
    }
    if (hasMT5DemoLoginConfig) {
      return ELoginAs.MT5_DEMO;
    }
    if (hasCentroidDemoLoginConfig) {
      return ELoginAs.CENTROID_DEMO;
    }
    return null;
  };

  private updateLiveDemoLoginContext = ({
    loginAsDemo,
    loginAsLive
  }: {
    loginAsDemo: boolean;
    loginAsLive: boolean;
  }) => {
    const { multiServerEnabled } = this.companyProfile ?? {};
    const {
      hasCompanyLoginConfig,
      hasMT4DemoLoginConfig,
      hasMT5DemoLoginConfig,
      hasMT4LiveLoginConfig,
      hasMT5LiveLoginConfig,
      hasCentroidLiveLoginConfig,
      hasCentroidDemoLoginConfig
    } = validateCompanyAccess(this.companyProfile);

    const demoLoginType =
      this.getDemoLoginTypeFromCompanyAccess({
        multiServerEnabled,
        hasMT4DemoLoginConfig,
        hasMT5DemoLoginConfig,
        hasCentroidDemoLoginConfig
      }) ?? ELoginAs.OMS_DEMO;
    // Switch from Live to Demo Account
    if (loginAsDemo) {
      this.setLoginAs(demoLoginType);
      return;
    }
    // Switch from Demo to Live Account
    if (loginAsLive) {
      const liveLoginType = hasCompanyLoginConfig
        ? ELoginAs.LIVE
        : authenticationViewController.getLiveLoginTypeFromCompanyAccess({
            multiServerEnabled,
            hasMT4LiveLoginConfig,
            hasMT5LiveLoginConfig,
            hasCentroidLiveLoginConfig
          });

      this.setLoginAs(liveLoginType || ELoginAs.LIVE);

      return;
    }
    // Login out without switching account type
    const isCurrentlyLoginAsDemo = this.loginAs && DEMO_LOGINS.includes(this.loginAs);
    if (isCurrentlyLoginAsDemo) {
      this.setLoginAs(demoLoginType);
    }
  };

  public setLoginAs = (loginAs: ELoginAs) => {
    authenticationService.setLoginAs(loginAs);
  };

  public updateAccountBalances = (params: Partial<IViewTradingAccountBalances>) => {
    authenticationService.setTradingAccountBalances(params);
  };

  public updateAccountBalanceAmountFromSocket = (params: ITradeCashPositionLiveData) => {
    const { amount } = params;

    authenticationService.setTradingAccountBalances({
      balance: amount
    });
  };

  public updateBuyingPowerFromSocket = (params: ITradeTradingPowerLiveData) => {
    const { buyingPower } = params;

    authenticationService.setTradingAccountBalances({
      buyingPower
    });
  };

  public loginWithExperienceToken = async (
    params: IExperienceLoginParams,
    loginAs: ELoginAs,
    onSuccess: () => void,
    onError: (message: string) => void
  ) => {
    authenticationService.setExperienceTokenLoading(true);
    await authenticationAPIController.loginWithExperienceToken(params, {
      onSuccess: async (result) => {
        this.setLoginAs(loginAs);
        if (result) {
          this.handleLoginResult({ result, successCallback: onSuccess });
          this.fetchAccount()
            .then(onSuccess)
            .catch((error) => onError(mapResponseCodeToMessage(error.code, error.message)));
        }
      },
      onError: (error) => {
        onError(mapResponseCodeToMessage(error.code, error.message));
      },
      onFinally: () => {
        authenticationService.setExperienceTokenLoading(false);
      }
    });
  };

  public loginWithExperienceExtendToken = async (
    params: IExperienceLoginParams,
    loginAs: ELoginAs,
    onExtendSuccess: () => void,
    onSuccess: () => void,
    onError: (message: string) => void
  ) => {
    authenticationService.setExperienceTokenLoading(true);
    await authenticationAPIController.loginWithExperienceExtendToken(params, {
      onSuccess: async () => {
        await this.loginWithExperienceToken(params, loginAs, onSuccess, onError);
        onExtendSuccess();
      },
      onError: (error) => {
        onError(mapResponseCodeToMessage(error.code, error.message));
      },
      onFinally: () => {
        authenticationService.setExperienceTokenLoading(false);
      }
    });
  };

  private startSessionInterval = () => {
    this.sessionInterval = setInterval(() => {
      this.checkSession();
    }, 5000);
  };

  private stopSessionInterval = async () => {
    const { sessionInterval } = this;

    if (checkIsNative()) {
      await this.unsubscribeFromFCMTopics();
      await messaging().deleteToken();
    }

    if (sessionInterval) {
      clearInterval(sessionInterval);
    }
  };

  private checkSession = async () => {
    const { checkingSession } = this;
    const { authenticated } = authenticationService.context;

    if (authenticated && !checkingSession) {
      this.checkingSession = true;

      await authenticationAPIController
        .extendSession({
          onSuccess: (code) => {
            if (code === ECommonResponseCode.UNAUTHORIZED) {
              this.logoutUser({});
            }
          },
          onError: (error) => {
            const { status, code } = error as IApiError;

            if (
              status?.toString() === ECommonResponseCode.UNAUTHORIZED ||
              // Tenant deactivated by root admin: status 400 + code C_MT_0001 (Invalid Tenant Setup)
              (code === ECommonResponseCode.C_MT_0001 && status?.toString() === ECommonResponseCode.BAD_REQUEST)
            ) {
              this.logoutUser({});
            }

            if (code === ECommonResponseCode.SI_1018) {
              snackbarController.showAlert({
                messageType: EUISnackBarMessageType.ERROR,
                message: mapResponseCodeToMessage(code)
              });
            }
          }
        })
        .finally(() => {
          this.checkingSession = false;
        });
    }
  };

  public changePasswordPCSubmit = async (
    params: IChangePasswordParams,
    { onSuccess, onError }: { onSuccess?: () => void; onError?: (error: IApiError) => void } = {}
  ) => {
    const { oldPassword, newPassword, confirmPassword } = params;

    authenticationService.setChangePasswordLoading(true);

    await authenticationAPIController.changePassword(
      {
        oldPassword,
        newPassword,
        confirmPassword
      },
      {
        onSuccess: () => {
          authenticationService.setChangePasswordLoading(false);

          snackbarController.showAlert({
            messageType: EUISnackBarMessageType.SUCCESS,
            message: i18n.t('Password has been changed.')
          });
          onSuccess?.();
        },
        onError: (error) => {
          authenticationService.setChangePasswordLoading(false);
          onError?.(error);
        }
      }
    );
  };

  public switchTradingAccount = async (
    account: ITradingAccount,
    callbacks: IAPIControllerCallbacks<ISwitchMetatraderAccount>
  ) => {
    const { serverId, metatraderId, simple2TradeAccountType, userGroup } = account;
    const { multiServerEnabled } = this.companyProfile ?? {};

    const params: ISwitchMetatraderAccountParams = {
      metatraderId
    };
    if (multiServerEnabled) {
      params.serverId = serverId;
    }
    if (this.loginAs !== ELoginAs.LIVE) {
      params.passwordType =
        simple2TradeAccountType === ESimple2TradeAccountType.INV
          ? EPasswordType.PASS_INVESTOR
          : EPasswordType.PASS_MAIN;
    }

    authenticationService.setSwitchingAccount(true);
    await userAccountAPIController.switchMetatraderAccount(params, {
      onSuccess: async (result) => {
        authenticationService.setSwitchingAccount(false);

        if (!result) {
          return;
        }
        mixpanelAnalysisService.user.switchAccount(account);

        storage.setItem(EStorageKey.ACCOUNT_SWITCHED, 'true');
        storage.setItem(EStorageKey.TRADING_ACCOUNT_IDENTITY, JSON.stringify({ metatraderId, serverId }));
        storage.setItem(EStorageKey.META_TRADER_GROUP, userGroup);
        if (result.switchedAccountToken) {
          storage.setItem(EStorageKey.TOKEN, result.switchedAccountToken);
        }

        broadcastTradingAccountChangeMessage({ metatraderId, serverId });

        await callbacks.onSuccess(result);
      },
      onError: callbacks.onError,
      onFinally: () => {
        authenticationService.setSwitchingAccount(false);
      }
    });
  };

  public fetchChangePassworldUrl = async () => {
    authenticationService.setFetchUrlPasswordLoading(true);

    try {
      const { result } = await accountAPI.getPasswordUrl();

      return {
        success: true,
        message: '',
        data: result
      };
    } catch (error) {
      const { code, message } = error as IApiError;

      return {
        success: false,
        message: mapResponseCodeToMessage(code, message)
      };
    } finally {
      authenticationService.setFetchUrlPasswordLoading(false);
    }
  };

  /** Private Methods */
  private findSelectedAccount = (tradingAccounts: ITradingAccount[]) => {
    return tradingAccounts.find((account) => account.selected) || tradingAccounts[0];
  };

  private resetStorage = () => {
    storage.removeItem(EStorageKey.TOKEN);
    storage.removeItem(EStorageKey.META_TRADER_GROUP);
    storage.removeItem(EStorageKey.SEARCH_HISTORY);
    storage.removeItem(EStorageKey.MARKET_SUBSCRIPTION);
    storage.removeItem(EStorageKey.LAST_SELECTED_SYMBOL);
    storage.removeItem(EStorageKey.LAST_SELECTED_SIGNAL_SYMBOL);
  };

  public syncRememberMeStateToStorage = (isRemembered: boolean) => {
    if (isRemembered) {
      storage.setItem(EStorageKey.REMEMBER_ME, true);
    } else {
      storage.removeItem(EStorageKey.REMEMBER_ME);
    }
  };

  public syncCredentialsToStorage = (
    args: { isRemembered: false } | { isRemembered: true; userId?: string; password?: string }
  ) => {
    if (args.isRemembered) {
      storage.setItem(EStorageKey.USER_ID, args.userId);
      storage.setItem(EStorageKey.PASSWORD, args.password);
    } else {
      storage.removeItem(EStorageKey.USER_ID);
      storage.removeItem(EStorageKey.PASSWORD);
    }
  };

  private getLoginAs = (loginConfig: IUserDetail['loginConfig']) => {
    switch (loginConfig) {
      case ELoginConfigType.COMPANY:
        return ELoginAs.LIVE;
      case ELoginConfigType.METATRADER4_DEMO:
        return ELoginAs.MT4_DEMO;
      case ELoginConfigType.METATRADER4_LIVE:
        return ELoginAs.MT4_LIVE;
      case ELoginConfigType.METATRADER5_DEMO:
        return ELoginAs.MT5_DEMO;
      case ELoginConfigType.METATRADER5_LIVE:
        return ELoginAs.MT5_LIVE;
      case ELoginConfigType.CENTROID_LIVE:
        return ELoginAs.CENTROID_LIVE;
      case ELoginConfigType.CENTROID_DEMO:
        return ELoginAs.CENTROID_DEMO;
      default:
        return ELoginAs.LIVE;
    }
  };

  /** Mobile FCM */
  // Android/iOS ONLY method: Subscribing of Firebase Messaging Topics when user logs in new device session
  public subscribeToFCMTopics = (topics: string[] | undefined) => {
    if (!topics || topics.length === 0) {
      return;
    }

    Promise.all(
      topics.map(async (topic) => {
        try {
          await messaging().subscribeToTopic(topic);
          this.updateSubscribedFCMTopics(topic, true);
        } catch (error) {
          // Intended DEV message
          // eslint-disable-next-line no-console
          console.error(`[FCM] - Failed to subscribe to FCM topic "${topic}":`, error);
        }
      })
    ).then(() => {
      // eslint-disable-next-line no-console
      console.debug('[FCM] - Subscribed to FCM topics', topics);
    });
  };

  // Android/iOS ONLY method: Unsubscribing of Firebase Messaging Topics when device session is invalid
  public unsubscribeFromFCMTopics = async () => {
    const storedFCMTopics = storage.getItem(EStorageKey.FCM_TOPICS) as string;
    const subscribedFCMTopics = storedFCMTopics?.split(',');
    if (!isEmpty(storedFCMTopics)) {
      await Promise.all(
        subscribedFCMTopics.map(async (topic) => {
          try {
            messaging().unsubscribeFromTopic(topic);
            this.updateSubscribedFCMTopics(topic, false);
          } catch (error) {
            // Intended DEV message
            // eslint-disable-next-line no-console
            console.error(`Failed to unsubscribe to FCM topic "${topic}":`, error);
          }
        })
      );
    }
  };

  private updateSubscribedFCMTopics = (topicToUpdate: string, subscribe: boolean) => {
    const storedFCMTopicString = (storage.getItem(EStorageKey.FCM_TOPICS) as string) || '';
    const subscribedFCMTopics = storedFCMTopicString?.split(',');
    let updatedFCMTopics = storedFCMTopicString;
    const isTopicSubscribed = subscribedFCMTopics.find((subscribedTopic) => subscribedTopic === topicToUpdate);
    if (subscribe) {
      if (isPropInvalid(isTopicSubscribed) && !isEmpty(storedFCMTopicString)) {
        // Add a new topic on top of existing topic list
        updatedFCMTopics = subscribedFCMTopics.join(',') + `,${topicToUpdate}`;
      } else if (isEmpty(storedFCMTopicString)) {
        // Add a new topic on top of empty topic list
        updatedFCMTopics = topicToUpdate;
      }
    } else if (!subscribe) {
      if (!isPropInvalid(isTopicSubscribed)) {
        updatedFCMTopics = subscribedFCMTopics.filter((subscribedTopic) => subscribedTopic !== topicToUpdate).join(',');
      }
    }
    storage.setItem(EStorageKey.FCM_TOPICS, updatedFCMTopics);
  };

  private resetUserDetails = () => {
    authenticationService.setAuthenticated(false);
    authenticationService.setTradingAccounts([]);
    authenticationService.setUserDetails(undefined);
  };

  private onFetchAccountError = (redirectToLogin: boolean) => {
    if (redirectToLogin) {
      authenticationService.setAuthenticated(false);
    }
    authenticationService.setLoading(false);
    this.stopSessionInterval();
  };
}

export const authenticationViewController = new AuthenticationViewController();
